import { ScaleElement } from '@scale/base-element';

const SWIPE_SLOP = 6;
const SWIPE_CLOSE_RATIO = 0.35;
const SWIPE_CLOSE_VELOCITY = 0.6;

const pageHolders = new Set();

function holdPage(holder, held) {
  if (held) pageHolders.add(holder);
  else pageHolders.delete(holder);
  document.documentElement.toggleAttribute('data-scroll-lock', pageHolders.size > 0);
}

export class DialogBase extends ScaleElement {
  static requiredRefs = ['dialog'];
  #popstateController;
  #swipe;

  get useHistory() {
    return false;
  }

  get dialogId() {
    return this.id;
  }

  get swipeToClose() {
    return false;
  }

  get holdsPage() {
    return false;
  }

  setup() {
    const dialog = this.ref('dialog');

    dialog.addEventListener('close', () => {
      this.#releaseSwipe(dialog);
      holdPage(this, false);
      this.onDialogClose();
    }, { signal: this.signal });

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.close();
    }, { signal: this.signal });

    if (this.swipeToClose) this.#setupSwipe(dialog);

    this.setupDialog();
  }

  setupDialog() {}

  disconnectedCallback() {
    super.disconnectedCallback();
    holdPage(this, false);
  }

  open() {
    const dialog = this.ref('dialog');
    if (dialog.open) return;

    const page = document.documentElement;
    page.toggleAttribute('data-scrollbar', innerWidth > page.clientWidth);
    if (this.holdsPage) holdPage(this, true);
    dialog.showModal();
    this.trapFocus(dialog);
    this.onDialogOpen();

    if (this.useHistory) {
      history.pushState({ drawerId: this.dialogId }, '');
      this.#popstateController?.abort();
      this.#popstateController = new AbortController();
      window.addEventListener('popstate', () => {
        if (dialog.open) this.close();
      }, { signal: this.#popstateController.signal, once: true });
    }
  }

  close() {
    const dialog = this.ref('dialog');
    if (!dialog.open) return;
    this.#popstateController?.abort();
    dialog.close();
  }

  #setupSwipe(dialog) {
    const options = { signal: this.signal };

    dialog.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      if (event.target.closest('input, select, textarea') || scrollsSideways(event.target, dialog)) return;
      this.#swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false };
    }, options);

    dialog.addEventListener('pointermove', (event) => {
      const swipe = this.#swipe;
      if (event.pointerId !== swipe?.id) return;
      const dx = event.clientX - swipe.x;

      if (!swipe.dragging) {
        const dy = event.clientY - swipe.y;
        if (Math.abs(dy) > SWIPE_SLOP && Math.abs(dy) > Math.abs(dx)) {
          this.#swipe = undefined;
          return;
        }
        if (Math.abs(dx) <= SWIPE_SLOP) return;
        const rect = dialog.getBoundingClientRect();
        Object.assign(swipe, {
          dragging: true,
          edge: rect.left + rect.right > window.innerWidth ? 1 : -1,
          width: rect.width,
          travel: 0,
          time: event.timeStamp,
          velocity: 0,
        });
        dialog.setPointerCapture(event.pointerId);
        dialog.style.transition = 'none';
      }

      const toward = dx * swipe.edge;
      const travel = toward > 0 ? toward : -Math.min(-toward / 4, 18);
      const elapsed = event.timeStamp - swipe.time;
      if (elapsed > 0) swipe.velocity = swipe.velocity * 0.6 + ((travel - swipe.travel) / elapsed) * 0.4;
      swipe.travel = travel;
      swipe.time = event.timeStamp;

      dialog.style.transform = `translateX(${travel * swipe.edge}px)`;
      dialog.style.setProperty('--swipe-progress', Math.max(travel, 0) / swipe.width);
    }, options);

    const release = (event) => {
      const swipe = this.#swipe;
      if (event.pointerId !== swipe?.id) return;
      const dismissed = swipe.dragging && event.type === 'pointerup'
        && (swipe.travel > swipe.width * SWIPE_CLOSE_RATIO || swipe.velocity > SWIPE_CLOSE_VELOCITY);
      this.#releaseSwipe(dialog);
      if (dismissed) this.close();
    };
    dialog.addEventListener('pointerup', release, options);
    dialog.addEventListener('pointercancel', release, options);
  }

  #releaseSwipe(dialog) {
    this.#swipe = undefined;
    dialog.style.removeProperty('transition');
    dialog.style.removeProperty('transform');
    dialog.style.removeProperty('--swipe-progress');
  }

  onDialogOpen() {}
  onDialogClose() {}

  onBlockSelect() { this.open(); }
  onBlockDeselect() { this.close(); }
}

function scrollsSideways(target, dialog) {
  for (let element = target; element && element !== dialog; element = element.parentElement) {
    if (element.scrollWidth > element.clientWidth && /auto|scroll/.test(getComputedStyle(element).overflowX)) return true;
  }
  return false;
}
